import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ProgressCircle } from '~/components/ProgressCircle/ProgressCircle';
import { Separator } from '~/components/Separator';
import { cn } from '~/lib/cn';
import { TEXT_COLORS } from '~/modules/artist-roster/components/BarChart/constants';
import type { StreamType } from '~/modules/artist-roster/components/LineGraph/LineGraph';
import { PerformanceDeltaLabel } from '~/modules/artist-roster/components/PerformanceDeltaLabel/PerformanceDeltaLabel';
import { formatMillionsNumber } from '~/modules/artist-roster/utils/formatMillionsNumber';

interface TooltipData {
  total: number;
  change: number;
}

interface TooltipBreakdownData {
  label: string;
  value: number;
}

interface BarChartTooltipProps {
  header: ReactNode | string;
  data: TooltipData;
  totalStreams: number;
  streamType: StreamType;
  breakdown?: TooltipBreakdownData[];
}

export function BarChartTooltip({
  header,
  data,
  totalStreams,
  streamType,
  breakdown,
}: BarChartTooltipProps) {
  const { t } = useTranslation('roster');

  return (
    <>
      <div className="mb-md flex w-fit gap-sm rounded-xs p-sm text-body-md font-bold bg-emphasis-default">
        {header}
      </div>
      <div className="mb-sm text-caption-md font-bold">
        {t(`trackDetails.tooltips.${streamType}`)}
      </div>
      <div className="mb-md flex">
        <div className="text-body-md">{data.total.toLocaleString()}</div>
        <PerformanceDeltaLabel
          delta={Math.round((data.change / data.total) * 100)}
          className="ml-sm"
          dateRange={t('trackDetails.labels.wow')}
        />
      </div>
      <div className="flex flex-wrap items-center gap-sm text-body-sm">
        <Trans
          t={t}
          i18nKey={`trackDetails.tooltips.percentOfTotal.${streamType}`}
          values={{
            percent: `${Math.round((data.total / totalStreams) * 100)}%`,
            streamCount: formatMillionsNumber(totalStreams),
          }}
          components={{
            ProgressCircle: (
              <ProgressCircle
                percent={Math.round((data.total / totalStreams) * 100)}
              />
            ),
            div: <div className="basis-full" />,
          }}
        />
      </div>
      {breakdown ? (
        <>
          <Separator className="my-md" />
          <div className="grid grid-cols-2 gap-md">
            {breakdown.map(({ label, value }, index) => {
              if (!index) {
                return null;
              }

              return (
                <div key={label} className="flex flex-col gap-sm">
                  <div className="text-caption-md font-bold">{label}</div>
                  <div className={cn('text-body-md', TEXT_COLORS[index - 1])}>
                    {value}%
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : null}
    </>
  );
}

BarChartTooltip.displayName = 'BarChartTooltip';
